import {createSelector} from 'reselect'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import {isPresent, isNumeric} from '../../../common/utils.js'
import api from '../../../common/api.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import TextInput from '../../../common/components/TextInput.js'
import TextArea from '../../../common/components/TextArea.js'
import DatePicker from '../../../common/components/DatePicker.js'
import Select from '../../../common/components/Select.js'
import CurrencyInput from '../../../common/components/CurrencyInput.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {refreshTransferList} from '../transferListFunctions.js'
import {setTransfer, transferSelector} from '../../../data/transfers.js'
import {buildPath} from '../../../common/querystring.js'
import {TRANSFER_URI_COMPONENT} from '../../../common/constants/Transfers.js'

export const MODAL_FORM = 'edit_transfer_param_modal'

export function showTransferNotesModal(transferID) {
  const transfer = transferSelector(getState(), {transferID})

  showEditTransferParamModal({
    transferID,
    value: transfer.transfer_notes,
    display: 'Notes',
    apiProp: 'transfer_notes',
    inputType: 'textarea',
  })
}

export function defaultCast(value) {
  return value
}

export function dateCast(value) {
  return value ? value.toISOString() : null
}

export function numberCast(value) {
  return Number(value)
}

export function showEditTransferParamModal({
  transferID,
  value,
  display,
  apiProp,
  isRequired,
  needsTransferListRefresh,
  inputType,
  typeCast,
  SubComponent,
  optionsSelector,
}) {
  inputType = inputType || 'text'

  setForm(MODAL_FORM, {
    transferID,
    apiProp,
    value:
      inputType === 'date'
        ? value
          ? new Date(value)
          : null
        : isPresent(value)
          ? value
          : '',
    display,
    isRequired: !!isRequired,
    needsTransferListRefresh: !!needsTransferListRefresh,
    inputType,
    typeCast:
      typeCast ||
      (inputType === 'date'
        ? dateCast
        : inputType === 'currency'
          ? numberCast
          : defaultCast),
    isSaving: false,
    serverError: null,
    SubComponent,
    optionsSelector,
  })
}

export function updateModalForm(...args) {
  return updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  return removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export const errorsSelector = createSelector(
  modalFormSelector,
  ({value, display, isRequired, inputType}) => {
    const errors = {}

    if (isRequired && !isPresent(value)) {
      errors.value = `${display} is required`
      errors.preventSave = true
    }

    if (inputType === 'currency' && !isNumeric(value)) {
      errors.value = `${display} must be a number`
      errors.preventSave = true
    }

    return errors
  },
)

export async function saveTransferParam() {
  try {
    const {
      transferID,
      value,
      display,
      apiProp,
      needsTransferListRefresh,
      typeCast,
    } = modalFormSelector(getState())

    const params = {
      [apiProp]: typeCast(value),
    }

    updateModalForm({isSaving: true, serverError: null})

    const {json} = await api.put(
      buildPath([TRANSFER_URI_COMPONENT, transferID]),
      params,
    )

    setTransfer(json)

    closeModal()

    showMessageToast(`Saved Transfer ${display.toLowerCase()}.`)

    if (needsTransferListRefresh) {
      await refreshTransferList()
    }
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function EditTransferParamModal({form}) {
  const errors = useSelector(errorsSelector)
  const options = useSelector((state) =>
    form.optionsSelector ? form.optionsSelector(state) : [],
  )

  return (
    <ConfirmModal
      title={`Edit ${form.display}`}
      modalSize="sm"
      onConfirm={() => saveTransferParam()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      error={form.serverError}
      preventInnerScroll
    >
      <div className="list__item--form list__item--no-style margin-bottom-20">
        <div className="fs-01">
          <strong>{form.transferID}</strong>
        </div>
      </div>
      {form.inputType === 'text' ? (
        <TextInput
          className="margin-bottom-0"
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        />
      ) : form.inputType === 'textarea' ? (
        <TextArea
          className="fs-00 lh-md margin-bottom-0"
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        />
      ) : form.inputType === 'date' ? (
        <DatePicker
          id="transfer_param_modal"
          selected={form.value}
          onChange={(value) =>
            updateModalForm({
              value,
            })
          }
        />
      ) : form.inputType === 'currency' ? (
        <CurrencyInput
          className="biggie margin-bottom-0"
          width="sm"
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        />
      ) : form.inputType === 'select' ? (
        <Select
          value={form.value}
          onChange={(value) => updateModalForm({value})}
          errorMessage={errors.value}
          autoFocus
        >
          {options.map(({value, display}) => (
            <option key={value} value={value}>
              {display}
            </option>
          ))}
        </Select>
      ) : null}
      {form.SubComponent && <form.SubComponent form={form} />}
    </ConfirmModal>
  )
}

export default onlyIfForm(EditTransferParamModal, modalFormSelector)
